import React, { useState } from "react";
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { COLORS, FONT } from "../../constants";
import { STEP1_FIELDS, inputSx } from "./constants";
import { Captcha } from "../Captcha";
import { sanitizeDigits, sanitizePhone } from "../../utils/helpers";
import DeliveryAddressModal from "./DeliveryAddressModal";

const AccountApplicationStep = ({ formData, handleChange, idType, setIdType, captchaBind, emailLocked }) => {
  const [deliveryAddressModalOpen, setDeliveryAddressModalOpen] = useState(false);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, flex: 1 }}>
      {STEP1_FIELDS.filter(
        (field) => field.key !== "vatNumber" || idType === "companyRegNo",
      ).map((field) => (
        <Box key={field.key}>
          <Typography
            sx={{
              fontSize: FONT.size.xs,
              fontWeight: 400,
              color: COLORS.darkText,
              mb: 0.75,
              letterSpacing: 0.5,
            }}
          >
            {field.label}
            {field.required && (
              <Box component="span" sx={{ color: COLORS.themeRed, ml: 0.25 }}>
                *
              </Box>
            )}
          </Typography>
          {field.key === "deliveryAddress" ? (
            <TextField
              fullWidth
              size="small"
              placeholder="Click to search for an address"
              value={formData.deliveryAddress}
              onClick={() => setDeliveryAddressModalOpen(true)}
              InputProps={{ readOnly: true }}
              sx={{ ...inputSx, "& .MuiInputBase-input": { ...inputSx["& .MuiInputBase-input"], cursor: "pointer" } }}
            />
          ) : (
            <TextField
              fullWidth
              size="small"
              type={field.type || "text"}
              value={formData[field.key]}
              onChange={(e) => {
                const { value } = e.target;
                if (field.key === "phone") {
                  handleChange(field.key, sanitizePhone(value));
                } else if (field.key === "vatNumber") {
                  handleChange(field.key, sanitizeDigits(value));
                } else {
                  handleChange(field.key, value);
                }
              }}
              disabled={field.key === "email" && emailLocked}
              sx={inputSx}
            />
          )}
        </Box>
      ))}

      {/* Company Reg No / ID No toggle */}
      <Box>
        <Typography
          sx={{
            fontSize: FONT.size.xs,
            fontWeight: 400,
            color: COLORS.darkText,
            mb: 0.5,
            letterSpacing: 0.5,
          }}
        >
          IDENTIFICATION
          <Box component="span" sx={{ color: COLORS.themeRed, ml: 0.25 }}>
            *
          </Box>
        </Typography>
        <RadioGroup
          row
          value={idType}
          onChange={(e) => setIdType(e.target.value)}
          sx={{ mb: 1 }}
        >
          <FormControlLabel
            value="companyRegNo"
            control={
              <Radio
                size="small"
                sx={{
                  color: "#cccccc",
                  "&.Mui-checked": { color: COLORS.themeRed },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: FONT.size.xs, color: COLORS.darkText }}>
                Company Reg No.
              </Typography>
            }
          />
          <FormControlLabel
            value="idNo"
            control={
              <Radio
                size="small"
                sx={{
                  color: "#cccccc",
                  "&.Mui-checked": { color: COLORS.themeRed },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: FONT.size.xs, color: COLORS.darkText }}>
                ID No.
              </Typography>
            }
          />
        </RadioGroup>
        <TextField
          fullWidth
          size="small"
          placeholder={
            idType === "companyRegNo"
              ? "e.g. 2023/123456/07"
              : "e.g. 9001010000000"
          }
          value={idType === "companyRegNo" ? formData.companyRegNo : formData.idNo}
          onChange={(e) =>
            handleChange(
              idType === "companyRegNo" ? "companyRegNo" : "idNo",
              idType === "companyRegNo"
                ? e.target.value
                : sanitizeDigits(e.target.value),
            )
          }
          sx={inputSx}
        />
      </Box>

      <Box>
        <Typography
          sx={{ fontSize: FONT.size.xs, fontWeight: 400, color: COLORS.darkText, mb: 0.75, letterSpacing: 0.5 }}
        >
          CAPTCHA VERIFICATION
          <Box component="span" sx={{ color: COLORS.themeRed, ml: 0.25 }}>*</Box>
        </Typography>
        <Captcha {...captchaBind} />
      </Box>

      <DeliveryAddressModal
        open={deliveryAddressModalOpen}
        onClose={() => setDeliveryAddressModalOpen(false)}
        onConfirm={(address, coordinates) => {
          handleChange("deliveryAddress", address);
          handleChange("deliveryAddressCoordinates", coordinates);
        }}
      />
    </Box>
  );
};

export default AccountApplicationStep;
